import { useState, useRef } from 'react';
import { SEOMeta } from '../components/common/SEOMeta';
import { SectionTitle } from '../components/common/SectionTitle';
import { Button } from '../components/common/Button';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { showSuccessAlert } from '../hooks/use-toast';
import Input from '../components/common/Input';
import '../assets/css/pages/Careers.css';

export default function Careers() {
  const headerReveal = useScrollReveal();
  const benefitsReveal = useScrollReveal();
  const jobsReveal = useScrollReveal();

  const [formState, setFormState] = useState({
    name: '',
    email: '',
    position: '',
    message: '',
    resume: null as File | null,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const benefits = [
    { icon: "fa-solid fa-laptop-code", title: "Modern Tech Stack", subtitle: "Work with the latest frameworks and tools." },
    { icon: "fa-solid fa-clock-rotate-left", title: "Flexible Hours", subtitle: "We value output over clocking hours." },
    { icon: "fa-solid fa-graduation-cap", title: "Continuous Learning", subtitle: "Budgets for courses, conferences, and certs." },
    { icon: "fa-solid fa-heart-pulse", title: "Health Benefits", subtitle: "Comprehensive health coverage for you and family." },
    { icon: "fa-solid fa-mug-hot", title: "Great Office", subtitle: "Ergonomic setups, premium coffee, and snacks." },
    { icon: "fa-solid fa-arrow-trend-up", title: "Fast Growth", subtitle: "Clear progression paths and mentorship." },
    { icon: "fa-solid fa-globe", title: "Remote Options", subtitle: "Hybrid work models available for senior roles." },
    { icon: "fa-solid fa-champagne-glasses", title: "Team Events", subtitle: "Regular hackathons, dinners, and offsites." },
    { icon: "fa-solid fa-bolt", title: "High Impact Work", subtitle: "Build products that directly influence real users." },
  ];

  const jobs = [
    { position: "Senior React Developer", location: "Rajkot / Hybrid", type: "Full-time", experience: "4+ Years Exp." },
    { position: "Node.js Backend Engineer", location: "Rajkot (On-site)", type: "Full-time", experience: "3+ Years Exp." },
    { position: "UI/UX Designer", location: "Rajkot / Remote", type: "Full-time", experience: "2+ Years Exp." },
    { position: "Mobile Developer (Flutter)", location: "Rajkot (On-site)", type: "Full-time", experience: "2+ Years Exp." },
  ];

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formState.name.trim()) newErrors.name = "Full name is required";

    if (!formState.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formState.email)) {
      newErrors.email = "Invalid email address format";
    }

    if (!formState.position) newErrors.position = "Select a position";

    if (!formState.resume) newErrors.resume = "Resume is required";

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    setTimeout(() => {
      showSuccessAlert("Application submitted successfully!");

      setFormState({
        name: '',
        email: '',
        position: '',
        message: '',
        resume: null,
      });

      setErrors({});

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setLoading(false);
    }, 1000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormState(prev => ({
      ...prev,
      [name]: value,
    }));

    setErrors(prev => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowed.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        resume: "Only PDF, DOC, DOCX allowed",
      }));
      return;
    }

    setFormState(prev => ({
      ...prev,
      resume: file,
    }));

    setErrors(prev => ({
      ...prev,
      resume: '',
    }));
  };

  return (
    <>
      <SEOMeta
        title="Careers"
        description="Join the engineering team at Logicraft Infotech. Explore open positions and our company culture."
        canonical="https://www.logicraftinfotech.com/careers"
      />

      <section className="page-header" ref={headerReveal.ref}>
        <div className={`container ${headerReveal.className}`}>
          <SectionTitle title="Join Our Command Center" centered />
          <div className="careers-intro">
            <p>
              We are always looking for exceptional talent. If you are passionate about writing clean code, solving complex architectural challenges, and building products that matter, you belong here.
            </p>
          </div>
        </div>
      </section>

      <section className="section" style={{ backgroundColor: 'var(--card-bg)' }} ref={benefitsReveal.ref}>
        <div className={`container ${benefitsReveal.className}`}>
          <SectionTitle title="Life at Logicraft Infotech" centered />
          <div className="benefits-grid">
            {benefits.map((benefit, i) => (
              <div className="benefit-card" key={i}>
                <div className="benefit-icon"><i className={benefit.icon}></i></div>
                <div className="benefit-content">
                  <h4>{benefit.title}</h4>
                  <p>{benefit.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" ref={jobsReveal.ref}>
        <div className={`container ${jobsReveal.className}`}>
          <SectionTitle title="Open Positions" />

          <div className="open-positions">
            <div className="job-list">
              {jobs.map((job, i) => (
                <div className="job-card" key={i}>
                  <div className="job-info">
                    <h3>{job.position}</h3>
                    <div className="job-meta">
                      <span><i className="fa-solid fa-location-dot"></i> {job.location}</span>
                      <span><i className="fa-solid fa-briefcase"></i> {job.type}</span>
                      <span><i className="fa-solid fa-code-commit"></i> {job.experience}</span>
                    </div>
                  </div>
                  <Button className="btn-outline" onClick={() => {
                    setFormState(prev => ({ ...prev, position: job.position }));
                    document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' });
                  }}>Apply Now</Button>
                </div>

              ))}
            </div>
          </div>

          <div id="apply-form" className="application-section">
            <h3>Submit Your Application</h3>
            <p>Don't see a perfect match? Send us your resume anyway.</p>

            <form className="application-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name"><i className="fa-solid fa-user"></i> Full Name</label>
                <input type="text" id="name" className="form-control" placeholder="John Doe" value={formState.name} onChange={handleChange} />
                {errors.name && <small className="error">{errors.name}</small>}
              </div>
              {/* <Input
                label="Full Name"
                name="name"
                value={formState.name}
                onChange={handleChange}
                error={errors.name}
              /> */}

              <div className="form-group">
                <label htmlFor="email"><i className="fa-solid fa-envelope"></i> Email Address</label>
                <input type="email" id="email" className="form-control" placeholder="john@example.com" value={formState.email} onChange={handleChange} />
                {errors.email && <small className="error">{errors.email}</small>}
              </div>

              <div className="form-group">
                <label htmlFor="position"><i className="fa-solid fa-briefcase"></i>Position Applied For</label>
                <select id="position" name="position" className="form-control" value={formState.position} onChange={handleChange}>
                  <option value="">Select a position...</option>
                  <option value="Senior React Developer">Senior React Developer</option>
                  <option value="Node.js Backend Engineer">Node.js Backend Engineer</option>
                  <option value="UI/UX Designer">UI/UX Designer</option>
                  <option value="Mobile Developer (Flutter)">Mobile Developer (Flutter)</option>
                  <option value="General Application">General Application</option>
                </select>
                {errors.position && <small className="error">{errors.position}</small>}
              </div>

              <div className="form-group">
                <label htmlFor="resume"><i className="fa-solid fa-cloud-arrow-up"></i> Resume / CV (PDF)</label>
                <input type="file" id="resume" className="form-control" name="resume" accept=".pdf,.doc,.docx" onChange={handleFileChange} ref={fileInputRef} />
                {errors.resume && <small className="error">{errors.resume}</small>}
              </div>
              <div className="form-group full-width">
                <label htmlFor="message"><i className="fa-solid fa-message"></i> Your Message</label>
                <textarea id="message" className="form-control" placeholder="Tell us why you'd be a great fit..." value={formState.message} onChange={handleChange}></textarea>
                {errors.message && <small className="error">{errors.message}</small>}
              </div>

              <div className="form-group full-width">
                <Button type="submit" className="btn-solid" loading={loading} disabled={loading}>
                  Submit Application
                </Button>
              </div>
            </form>
          </div>

        </div>
      </section>
    </>
  );
}